/**
 * PublicOrgContext — gör hostname-baserad org-kontext tillgänglig för hela appen.
 *
 * Wrappas FÖRE AuthProvider så att:
 *   - Anonyma besökare får branding/signup-scope baserat på hostname
 *   - Signup kan INSERTa customer-rad med rätt organization_id
 *   - UnknownHostname-vy kan visas utan auth
 *
 * Ref: audit-reports/feat-domain-mapping/rapport.md
 */
import { createContext, useContext, ReactNode } from "react";
import { usePublicOrg, type PublicOrg } from "@/hooks/usePublicOrg";

interface PublicOrgContextValue {
  org: PublicOrg | null;
  isLoading: boolean;
  isUnknownHostname: boolean;
}

const PublicOrgContext = createContext<PublicOrgContextValue | null>(null);

export function PublicOrgProvider({ children }: { children: ReactNode }) {
  const { data: org, isLoading } = usePublicOrg();

  const value: PublicOrgContextValue = {
    org: org ?? null,
    isLoading,
    isUnknownHostname: !isLoading && !org,
  };

  return (
    <PublicOrgContext.Provider value={value}>
      {children}
    </PublicOrgContext.Provider>
  );
}

export function usePublicOrgContext() {
  const ctx = useContext(PublicOrgContext);
  if (!ctx) {
    throw new Error("usePublicOrgContext must be used within PublicOrgProvider");
  }
  return ctx;
}
